import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { get } from 'utils/lodash'
import { handleError } from 'utils/error'
import { putIsLoading } from 'middleware/actions/response'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { isAllowNegativeInvoiceFeatureAvailable } from 'data/launch-darkly/featureSelectors'
import { useSendInvoiceListMutation } from 'api/billing/sendInvoiceList'
import { usePostInvoiceListMutation } from 'api/billing/postInvoiceList'
import { usePostAndSendInvoiceListMutation } from 'api/billing/postAndSendInvoiceList'
import { useRegenerateInvoiceListMutation } from 'api/billing/regenerateInvoiceList'
import { useRegenerateAndRepostInvoiceMutation } from 'api/billing/regenerateAndRepostInvoice'
import { useRevertInvoiceToDraftMutation } from 'api/billing/revertInvoiceToDraft'
import { useUnpayInvoiceMutation } from 'api/billing/unpayInvoice'
import { useDeleteInvoicesFromGroupMutation } from 'api/groups/invoice/deleteInvoicesFromGroup'
import { useDeleteInvoicesMutation, useDeleteUnpaidInvoiceMutation } from 'api/billing/deleteInvoices'
import { setInvoicesPaymentsState } from 'slices/billing/invoicesSlice'
import { isUnPaidInvoice, getInvoicesPDFStatus } from 'utils/billing'
import T from 'T'
import { useAccountingLockValue } from 'providers/AccountingLockProvider'
import { INVOICE_STATUS } from 'settings/constants/billing'
import { getInvoiceActions } from './invoiceActionConfig'
import { CONFIRMATION_ACTION_KEYS } from './invoiceActionKeys'

const TIMEOUT = 4000

/**
 * Core invoice actions hook with business logic and API functionality
 *
 * @param {Object} selectedInvoice - The invoice object to perform actions on
 * @param {Object} options - Configuration options
 * @param {boolean} [options.showAddToGroupAction=true] - Whether to include group actions
 * @param {Function} [options.handleRefetch] - Callback to refresh data after actions
 * @param {Function} [options.handleRegenerateRefetch=handleRefetch] - Callback after regeneration actions;
 *   defaults to handleRefetch. Override when regeneration needs distinct side effects (e.g. start polling).
 * @param {Function} [options.getInvoiceId] - Function to extract invoice ID from invoice object
 * @param {Function} [options.getInvoiceNumber] - Function to extract invoice number from invoice object
 *
 * @returns {Object} { availableActions, coreActions, context, loadingStates }
 */
export const useInvoiceActionsCore = (selectedInvoice = {}, options = {}) => {
  const {
    showAddToGroupAction = true,
    handleRefetch = () => {},
    handleRegenerateRefetch = handleRefetch,
    getInvoiceId = invoice => get(invoice, 'id'),
    getInvoiceNumber = invoice => get(invoice, 'invoiceNumber'),
  } = options

  const dispatch = useDispatch()
  const flags = useFlags()
  const { checkIsLocked } = useAccountingLockValue()

  const deleteInvoiceConfirmation = useSelector(state => state.invoices.deleteInvoiceConfirmation)
  const deleteUnpaidInvoiceConfirmation = useSelector(state => state.invoices.deleteUnpaidInvoiceConfirmation)
  const revertToDraftConfirmation = useSelector(state => state.invoices.revertToDraftConfirmation)
  const revertCreditMemoToDraftConfirmation = useSelector(state => state.invoices.revertCreditMemoToDraftConfirmation)
  const regenerateConfirmation = useSelector(state => state.invoices.regenerateConfirmation)

  const [sendInvoiceList, { isLoading: isSendInvoiceListLoading }] = useSendInvoiceListMutation()
  const [postInvoiceList, { isLoading: isPostInvoiceListLoading }] = usePostInvoiceListMutation()
  const [postAndSendInvoiceList, { isLoading: isPostAndSendInvoiceListLoading }] = usePostAndSendInvoiceListMutation()
  const [regenerateInvoiceList, { isLoading: isRegenerateInvoiceListLoading }] = useRegenerateInvoiceListMutation()
  const [regenerateAndRepostInvoice, { isLoading: isRegenerateAndRepostLoading }] = useRegenerateAndRepostInvoiceMutation()
  const [revertToDraft, { isLoading: isRevertToDraftLoading }] = useRevertInvoiceToDraftMutation()
  const [unpayInvoice, { isLoading: isUnpayInvoiceLoading }] = useUnpayInvoiceMutation()
  const [deleteInvoicesFromGroup, { isLoading: isDeleteInvoicesFromGroupLoading }] = useDeleteInvoicesFromGroupMutation()
  const [deleteInvoices, { isLoading: isDeleteInvoicesLoading }] = useDeleteInvoicesMutation()
  const [deleteUnpaidInvoice, { isLoading: isDeleteUnpaidInvoiceLoading }] = useDeleteUnpaidInvoiceMutation()

  const groupId = get(selectedInvoice, 'groupId')
  const invoiceId = getInvoiceId(selectedInvoice)
  const invoiceNumber = getInvoiceNumber(selectedInvoice)

  const isAllowNegativeInvoiceEnabled = isAllowNegativeInvoiceFeatureAvailable({ flags })

  const context = {
    showAddToGroupAction,
    isAllowNegativeInvoiceEnabled,
    checkIsLocked,
    groupId,
  }

  const availableActions = getInvoiceActions(selectedInvoice, context)

  const handleChangeCanTakePayment = ({ timer }) => {
    if (timer) {
      setTimeout(() => {
        dispatch(setInvoicesPaymentsState({ canTakePayment: true }))
      }, TIMEOUT)
    }
  }

  const handleAPISuccess = (message, refetch = false, timer = false, refetchFn = handleRefetch) => {
    handleChangeCanTakePayment({ timer })
    if (timer) {
      toast.success(message, {
        hideProgressBar: false,
        autoClose: TIMEOUT,
        onClose: () => {
          if (refetch) {
            refetchFn()
          }
        },
      })
      return
    }

    toast.success(message)
    if (refetch) {
      refetchFn()
    }
  }

  const handleSendInvoices = async (invoiceNumbers, invoiceIds, successMessage, refetch = false, timer = false) => {
    let withPDFNumbers = invoiceNumbers
    let withPDFIds = invoiceIds
    let withoutPDF = []
    const isDraftStatus = selectedInvoice.status === INVOICE_STATUS.PARTIAL
    if (isDraftStatus) {
      const { withPDF: _withPDF, withoutPDF: _withoutPDF } = await getInvoicesPDFStatus(invoiceNumbers)
      withPDFNumbers = _withPDF
      withoutPDF = _withoutPDF
      // Filter invoiceIds to match only those invoiceNumbers that have PDFs
      withPDFIds = invoiceIds.filter((_, index) => withPDFNumbers.includes(invoiceNumbers[index]))
    }

    const hasInvoicesWithoutPDF = withoutPDF.length > 0

    if (withPDFIds.length) {
      try {
        await sendInvoiceList({ invoiceIds: withPDFIds }).unwrap()
        handleAPISuccess(successMessage, refetch, timer)
      } catch (error) {
        handleError(error)
      }
    } else if (hasInvoicesWithoutPDF) {
      throw new Error('Invoice PDF not available')
    }
  }

  const coreActions = {
    regenerate: async () => {
      if (isUnPaidInvoice(selectedInvoice?.status)) {
        return { requiresConfirmation: true, action: CONFIRMATION_ACTION_KEYS.CONFIRM_REGENERATE }
      }

      dispatch(putIsLoading(true))
      try {
        await regenerateInvoiceList({
          invoiceIds: [invoiceId],
          // Late fees already billed to this invoice come back on their own; this flag only sweeps the account's unbilled ones.
          includeLateFees: false,
        }).unwrap()
        handleAPISuccess(T.REGENERATE_INVOICES_SUCCESS_MSG, true, true, handleRegenerateRefetch)
        return { success: true }
      } catch (error) {
        handleError(error)
        return { error: true }
      } finally {
        dispatch(putIsLoading(false))
      }
    },
    confirmRegenerate: async () => {
      dispatch(putIsLoading(true))
      try {
        // Use invoice IDs from Redux state (set by data grid row actions) for confirmation actions
        const confirmationInvoiceIds = regenerateConfirmation.invoiceIds.length > 0 ? regenerateConfirmation.invoiceIds : [invoiceId]

        await regenerateInvoiceList({
          invoiceIds: confirmationInvoiceIds,
          // Late fees already billed to this invoice come back on their own; this flag only sweeps the account's unbilled ones.
          includeLateFees: false,
        }).unwrap()
        handleAPISuccess(T.REGENERATE_INVOICES_SUCCESS_MSG, true, true, handleRegenerateRefetch)
      } catch (error) {
        handleError(error)
      } finally {
        dispatch(putIsLoading(false))
      }
    },
    regenerateAndRepost: async () => {
      try {
        dispatch(putIsLoading(true))
        await regenerateAndRepostInvoice({ invoiceIds: [invoiceId] }).unwrap()
        handleAPISuccess(T.REGENERATE_AND_REPOST_INVOICE_SUCCESS, true, true, handleRegenerateRefetch)
      } catch (error) {
        handleError(error)
      } finally {
        dispatch(putIsLoading(false))
      }
    },

    post: async () => {
      dispatch(putIsLoading(true))
      try {
        await postInvoiceList({ invoiceIds: [invoiceId] }).unwrap()
        handleAPISuccess(T.POST_SINGLE_INVOICE_SUCCESS, true, true)
      } catch (error) {
        toast.error(T.FAILED)
        handleError(error)
      } finally {
        dispatch(putIsLoading(false))
      }
    },

    postAndSend: async () => {
      try {
        dispatch(putIsLoading(true))
        await postAndSendInvoiceList({ invoiceIds: [invoiceId] }).unwrap()
        handleAPISuccess(T.POST_AND_SEND_INVOICE_SUCCESS, true, true)
      } catch (error) {
        handleError(error)
      } finally {
        dispatch(putIsLoading(false))
      }
    },

    makePayment: () => {
      const invoiceData = {
        ...selectedInvoice,
        accountId: selectedInvoice.accountId,
        invoiceNumber: selectedInvoice.invoiceNumber,
        accountName: selectedInvoice.accountName || selectedInvoice.customerName,
        invoiceId,
        invoiceStatus: selectedInvoice.status,
        unPostedAmountCents: selectedInvoice.totalCents || selectedInvoice.amountCents || selectedInvoice.unPostedAmountCents,
      }

      dispatch(
        setInvoicesPaymentsState({
          isOpenTakePaymentsDialog: true,
          selectedRow: invoiceData,
        })
      )
    },

    email: async () => {
      try {
        dispatch(putIsLoading(true))
        await handleSendInvoices([invoiceNumber], [invoiceId], T.SEND_INVOICE_SUCCESS, true, true)
      } catch (error) {
        handleError(error)
      } finally {
        dispatch(putIsLoading(false))
      }
    },

    confirmRevertToDraft: async () => {
      dispatch(putIsLoading(true))

      try {
        // Use invoice ID from Redux state (set by data grid row actions) for confirmation actions
        const confirmationInvoiceId = revertToDraftConfirmation.invoiceId || invoiceId
        await revertToDraft({ id: confirmationInvoiceId }).unwrap()
        handleAPISuccess(T.REVERT_INVOICE_TO_DRAFT_SUCCESS, true, true)
      } catch (error) {
        handleError(error)
      } finally {
        dispatch(putIsLoading(false))
      }
    },

    confirmRevertCreditMemoToDraft: async () => {
      dispatch(putIsLoading(true))

      try {
        // Use invoice ID from Redux state (set by data grid row actions) for confirmation actions
        const confirmationInvoiceId = revertCreditMemoToDraftConfirmation.invoiceId || invoiceId
        await unpayInvoice({ invoiceId: confirmationInvoiceId }).unwrap()
        await revertToDraft({ id: confirmationInvoiceId }).unwrap()
        handleAPISuccess(T.REVERT_CREDIT_MEMO_TO_DRAFT_SUCCESS, true, true)
      } catch (error) {
        handleError(error)
      } finally {
        dispatch(putIsLoading(false))
      }
    },

    confirmDelete: async () => {
      dispatch(putIsLoading(true))
      try {
        // Use invoice IDs from Redux state (set by data grid row actions) for confirmation actions
        const confirmationInvoiceIds = deleteInvoiceConfirmation.invoiceIds.length > 0 ? deleteInvoiceConfirmation.invoiceIds : [invoiceId]

        await deleteInvoices({ invoiceIds: confirmationInvoiceIds }).unwrap()
        handleAPISuccess(T.DELETE_INVOICES_SUCCESS_MSG, true, true)
      } catch (error) {
        handleError(error)
      } finally {
        dispatch(putIsLoading(false))
      }
    },

    confirmDeleteUnpaid: async () => {
      dispatch(putIsLoading(true))
      try {
        // Use invoice ID from Redux state (set by data grid row actions) for confirmation actions
        const confirmationInvoiceId = deleteUnpaidInvoiceConfirmation.invoiceId || invoiceId
        await deleteUnpaidInvoice({ id: confirmationInvoiceId }).unwrap()
        handleAPISuccess(T.DELETE_INVOICE_SUCCESS_MSG, true, true)
      } catch (error) {
        handleError(error)
      } finally {
        dispatch(putIsLoading(false))
      }
    },

    removeFromGroup: async () => {
      dispatch(putIsLoading(true))
      try {
        await deleteInvoicesFromGroup({ invoiceGroupId: groupId, invoiceIds: [invoiceId] }).unwrap()
        handleAPISuccess(T.REMOVE_INVOICE_FROM_GROUP_SUCCESS_MSG, true, true)
      } catch (error) {
        handleError(error)
      } finally {
        dispatch(putIsLoading(false))
      }
    },
  }

  return {
    availableActions,
    coreActions,
    context,
    loadingStates: {
      isSendInvoiceListLoading,
      isPostInvoiceListLoading,
      isPostAndSendInvoiceListLoading,
      isRegenerateInvoiceListLoading,
      isRegenerateAndRepostLoading,
      isRevertToDraftLoading,
      isDeleteInvoicesFromGroupLoading,
      isDeleteInvoicesLoading,
      isDeleteUnpaidInvoiceLoading,
      isUnpayInvoiceLoading,
    },
  }
}
